import { useEffect, useMemo, useRef, useState } from 'react';
import { AdminUser } from '../../pm/types';
import { displayName } from '../../pm/constants';
import MentionPicker from './MentionPicker';

interface Props {
  admins: AdminUser[];
  placeholder?: string;
  submitLabel?: string;
  autoFocus?: boolean;
  onCancel?: () => void;
  onSubmit: (body: string, mentions: string[]) => Promise<void> | void;
}

interface ActiveMention {
  query: string;
  rangeStart: number; // index of '@' in textarea value
}

function findActiveMention(value: string, caret: number): ActiveMention | null {
  // Walk backwards from caret to find @ with no whitespace between.
  let i = caret - 1;
  while (i >= 0) {
    const ch = value[i];
    if (ch === '@') {
      // Only treat as a mention trigger if @ is at start or preceded by whitespace.
      const prev = i === 0 ? '' : value[i - 1];
      if (i === 0 || /\s/.test(prev)) {
        return { query: value.slice(i + 1, caret), rangeStart: i };
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
    i--;
  }
  return null;
}

const CommentComposer: React.FC<Props> = ({
  admins,
  placeholder = 'Add a comment…',
  submitLabel = 'Post',
  autoFocus = false,
  onCancel,
  onSubmit,
}) => {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [activeMention, setActiveMention] = useState<ActiveMention | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentioned, setMentioned] = useState<string[]>([]); // uids
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const filteredAdmins = useMemo(() => {
    if (!activeMention) return [];
    const q = activeMention.query.toLowerCase();
    if (!q) return admins.slice(0, 8);
    return admins
      .filter((a) => {
        const name = displayName(a.email).toLowerCase();
        return name.startsWith(q) || a.email.toLowerCase().includes(q);
      })
      .slice(0, 8);
  }, [activeMention, admins]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [activeMention?.query]);

  function recomputeMention(value: string, caret: number) {
    setActiveMention(findActiveMention(value, caret));
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value;
    setBody(value);
    recomputeMention(value, e.target.selectionStart ?? value.length);
  }

  function handleSelect(e: React.SyntheticEvent<HTMLTextAreaElement>) {
    const t = e.currentTarget;
    recomputeMention(t.value, t.selectionStart ?? t.value.length);
  }

  function pickMention(admin: AdminUser) {
    if (!activeMention || !textareaRef.current) return;
    const before = body.slice(0, activeMention.rangeStart);
    const caret = textareaRef.current.selectionStart ?? body.length;
    const after = body.slice(caret);
    const insertion = `@${displayName(admin.email)} `;
    const newValue = before + insertion + after;
    const newCaret = before.length + insertion.length;
    setBody(newValue);
    setMentioned((prev) => (prev.includes(admin.uid) ? prev : [...prev, admin.uid]));
    setActiveMention(null);
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(newCaret, newCaret);
    });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (activeMention && filteredAdmins.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => (i + 1) % filteredAdmins.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => (i - 1 + filteredAdmins.length) % filteredAdmins.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pickMention(filteredAdmins[selectedIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setActiveMention(null);
        return;
      }
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      // Only keep mentioned uids whose @DisplayName still appears in the body —
      // handles the case where a user deleted the inserted mention text.
      const finalMentions = mentioned.filter((uid) => {
        const a = admins.find((x) => x.uid === uid);
        if (!a) return false;
        return trimmed.includes(`@${displayName(a.email)}`);
      });
      await onSubmit(trimmed, finalMentions);
      setBody('');
      setMentioned([]);
      setActiveMention(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="pm-comment-composer" onSubmit={handleSubmit}>
      <div className="pm-mention-wrap">
        <textarea
          ref={textareaRef}
          placeholder={placeholder}
          value={body}
          onChange={handleChange}
          onSelect={handleSelect}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Defer so an onMouseDown pick can fire before we hide the menu.
            window.setTimeout(() => setActiveMention(null), 100);
          }}
          autoFocus={autoFocus}
          rows={2}
        />
        {activeMention ? (
          <MentionPicker
            admins={filteredAdmins}
            selectedIndex={selectedIndex}
            onPick={pickMention}
            onHover={setSelectedIndex}
          />
        ) : null}
      </div>
      <div className="pm-comment-composer-actions">
        <span className="pm-comment-composer-hint">Type @ to mention</span>
        {onCancel ? (
          <button type="button" className="pm-link-button" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button
          type="submit"
          className="pm-button pm-button-primary"
          disabled={busy || !body.trim()}
        >
          {busy ? 'Posting…' : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default CommentComposer;
